
import { useState } from 'react';
import Button from '@/components/base/Button';

export default function PricingSection() {
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      name: 'Starter Funnel',
      description: 'Perfect for small businesses getting started',
      monthlyPrice: 995,
      annualPrice: 9950,
      features: [
        '1 Complete Sales Funnel',
        'Up to 5 Pages',
        'Mobile Responsive Design',
        'Basic Email Integration',
        'Analytics Setup',
        '7-Day Delivery',
        '90-Day Support'
      ],
      popular: false,
      cta: 'Get Started'
    },
    {
      name: 'Professional Funnel',
      description: 'Most popular for growing businesses',
      monthlyPrice: 1495,
      annualPrice: 14950,
      features: [
        '1 Complete Sales Funnel',
        'Up to 10 Pages',
        'Advanced Automation',
        'A/B Testing Setup',
        'CRM Integration',
        'Email & SMS Sequences',
        '7-10 Day Delivery',
        '90-Day Support'
      ],
      popular: true,
      cta: 'Most Popular'
    },
    {
      name: 'Enterprise Package',
      description: 'For businesses ready to scale',
      monthlyPrice: 1995,
      annualPrice: 19950,
      features: [
        '2 Complete Sales Funnels',
        'Unlimited Pages',
        'Advanced Automation',
        'Multi-Platform Integration',
        'Conversion Optimization',
        'Priority Support',
        '7-14 Day Delivery',
        '90-Day Support'
      ],
      popular: false,
      cta: 'Scale Your Business'
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Choose the perfect plan for your business. All plans include our 100% money-back guarantee.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4">
            <span className={`text-sm font-medium ${!isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
              One-Time Payment
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none cursor-pointer ${
                isAnnual ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                  isAnnual ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
              Annual Package
            </span>
            {isAnnual && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                Save 20%
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden ${
                plan.popular ? 'ring-2 ring-blue-600 scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-blue-600 text-white text-center py-2 text-sm font-medium">
                  Most Popular
                </div>
              )}
              
              <div className={`p-8 ${plan.popular ? 'pt-12' : ''}`}>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-6">{plan.description}</p>
                
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900">
                      ${isAnnual ? plan.annualPrice.toLocaleString() : plan.monthlyPrice.toLocaleString()}
                    </span>
                    <span className="text-gray-600 ml-2">
                      {isAnnual ? '/year' : '/project'}
                    </span>
                  </div>
                  {isAnnual && (
                    <div className="text-sm text-green-600 mt-1">
                      Save ${((plan.monthlyPrice * 12) - plan.annualPrice).toLocaleString()} per year
                    </div>
                  )}
                </div>
                
                <Button
                  variant={plan.popular ? 'primary' : 'outline'}
                  size="lg"
                  className="w-full mb-8 whitespace-nowrap"
                >
                  {plan.cta}
                </Button>
                
                <div className="space-y-4">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <div className="inline-flex items-center px-6 py-3 bg-green-50 rounded-full mb-4">
            <i className="ri-shield-check-line text-green-600 mr-2"></i>
            <span className="text-green-800 font-medium">100% Money Back Guarantee</span>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            Not satisfied with your funnel? Get your money back within 30 days, no questions asked.
          </p>
          
          <div className="mb-8">
            <a 
              href="/pricing" 
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line mr-2"></i>
              Click for more details
            </a>
          </div>
          
          <div className="mt-8 p-6 bg-blue-50 rounded-xl max-w-3xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <i className="ri-time-line text-blue-600 text-xl"></i>
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-gray-900">Need it faster?</h3>
                <p className="text-gray-600">Priority 3-day delivery available</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Got a tight deadline? We can prioritize your project for 3-day delivery.
            </p>
            <Button variant="outline" className="whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>
              Book a Call for Priority Build
            </Button>
          </div>
        </div>

      </div>
    </section>
  );
}
